import React, { useState, useEffect } from 'react';
import {
  X, Save, Copy, Trash2, Play, ChevronDown, ChevronRight,
  Settings, Zap, Brain, Database, Mail, AlertCircle
} from 'lucide-react';
import { Button, TextField, Select, MenuItem, Switch, Slider, Chip } from '@material-ui/core';
import { i18n } from "../translate/i18n";

const NodeConfigPanel = ({ node, onClose, onSave, onDelete, onDuplicate }) => {
  const [config, setConfig] = useState(node?.data?.config || {});
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    advanced: false,
    output: false
  });

  if (!node) return null;

  const handleSave = () => {
    onSave(node.id, { ...node.data, config });
    onClose();
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderConfigByType = () => {
    const type = node.type;
    const subtype = node.data?.subtype;

    // AI Nodes Configuration
    if (type === 'ai') {
      if (subtype === 'gpt4' || subtype === 'claude' || subtype === 'gemini') {
        return (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.model")}
              </label>
              <Select
                value={config.model || 'gpt-4-turbo'}
                onChange={(e) => setConfig({ ...config, model: e.target.value })}
                fullWidth
                variant="outlined"
                style={{ fontSize: 13 }}
              >
                <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
                <MenuItem value="gpt-4">GPT-4</MenuItem>
                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                <MenuItem value="claude-3-opus">Claude 3 Opus</MenuItem>
                <MenuItem value="claude-3-sonnet">Claude 3 Sonnet</MenuItem>
                <MenuItem value="gemini-pro">Gemini Pro</MenuItem>
              </Select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.systemPrompt")}
              </label>
              <TextField
                multiline
                rows={4}
                value={config.systemPrompt || ''}
                onChange={(e) => setConfig({ ...config, systemPrompt: e.target.value })}
                placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.systemPrompt")}
                fullWidth
                variant="outlined"
                style={{ fontSize: 13 }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.temperatureLabel", {
                  value: config.temperature || 0.7,
                })}
              </label>
              <Slider
                value={config.temperature || 0.7}
                onChange={(e, value) => setConfig({ ...config, temperature: value })}
                min={0}
                max={2}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' }
                ]}
              />
              <p style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.helpers.temperature")}
              </p>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.maxTokens")}
              </label>
              <TextField
                type="number"
                value={config.maxTokens || 1000}
                onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) })}
                fullWidth
                variant="outlined"
              />
            </div>
          </>
        );
      }

      if (subtype === 'intent_detection') {
        return (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.intents")}
              </label>
              <TextField
                value={config.intents || i18n.t("flowBuilderCanvas.nodeConfig.defaults.intents")}
                onChange={(e) => setConfig({ ...config, intents: e.target.value })}
                fullWidth
                variant="outlined"
                placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.intents")}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.confidenceLabel", {
                  value: config.confidence || 0.7,
                })}
              </label>
              <Slider
                value={config.confidence || 0.7}
                onChange={(e, value) => setConfig({ ...config, confidence: value })}
                min={0}
                max={1}
                step={0.05}
              />
            </div>
          </>
        );
      }
    }

    // Trigger Nodes
    if (type === 'trigger') {
      if (subtype === 'webhook') {
        return (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.webhookUrl")}
              </label>
              <TextField
                value={config.webhookUrl || `https://klorum.com/webhook/${node.id}`}
                fullWidth
                variant="outlined"
                disabled
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={() => navigator.clipboard.writeText(config.webhookUrl)}
                    >
                      <Copy size={16} />
                    </Button>
                  )
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.httpMethod")}
              </label>
              <Select
                value={config.method || 'POST'}
                onChange={(e) => setConfig({ ...config, method: e.target.value })}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.requireAuth")}
                <Switch
                  checked={config.requireAuth || false}
                  onChange={(e) => setConfig({ ...config, requireAuth: e.target.checked })}
                  color="primary"
                />
              </label>
            </div>

            {config.requireAuth && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.fields.secretKey")}
                </label>
                <TextField
                  value={config.secretKey || ''}
                  onChange={(e) => setConfig({ ...config, secretKey: e.target.value })}
                  fullWidth
                  variant="outlined"
                  type="password"
                  placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.secretKey")}
                />
              </div>
            )}
          </>
        );
      }

      if (subtype === 'schedule') {
        return (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.scheduleType")}
              </label>
              <Select
                value={config.scheduleType || 'cron'}
                onChange={(e) => setConfig({ ...config, scheduleType: e.target.value })}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="cron">{i18n.t("flowBuilderCanvas.nodeConfig.options.scheduleType.cron")}</MenuItem>
                <MenuItem value="interval">{i18n.t("flowBuilderCanvas.nodeConfig.options.scheduleType.interval")}</MenuItem>
                <MenuItem value="specific">{i18n.t("flowBuilderCanvas.nodeConfig.options.scheduleType.specific")}</MenuItem>
              </Select>
            </div>

            {config.scheduleType === 'cron' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.fields.cronExpression")}
                </label>
                <TextField
                  value={config.cronExpression || '0 9 * * *'}
                  onChange={(e) => setConfig({ ...config, cronExpression: e.target.value })}
                  fullWidth
                  variant="outlined"
                  placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.cronExpression")}
                />
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.helpers.cronExpression")}
                </p>
              </div>
            )}

            {config.scheduleType === 'interval' && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <TextField
                  type="number"
                  value={config.intervalValue || 5}
                  onChange={(e) => setConfig({ ...config, intervalValue: parseInt(e.target.value) })}
                  variant="outlined"
                  style={{ width: 100 }}
                />
                <Select
                  value={config.intervalUnit || 'minutes'}
                  onChange={(e) => setConfig({ ...config, intervalUnit: e.target.value })}
                  variant="outlined"
                  style={{ flex: 1 }}
                >
                  <MenuItem value="minutes">{i18n.t("flowBuilderCanvas.nodeConfig.options.intervalUnit.minutes")}</MenuItem>
                  <MenuItem value="hours">{i18n.t("flowBuilderCanvas.nodeConfig.options.intervalUnit.hours")}</MenuItem>
                  <MenuItem value="days">{i18n.t("flowBuilderCanvas.nodeConfig.options.intervalUnit.days")}</MenuItem>
                </Select>
              </div>
            )}
          </>
        );
      }
    }

    // Communication Nodes
    if (type === 'communication') {
      if (subtype === 'whatsapp_send' || subtype === 'whatsapp_template') {
        return (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.messageTemplate")}
              </label>
              <TextField
                multiline
                rows={6}
                value={config.message || ''}
                onChange={(e) => setConfig({ ...config, message: e.target.value })}
                placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.messageTemplate")}
                fullWidth
                variant="outlined"
              />
              <p style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.helpers.variables", {
                  variable: "{{name}}",
                })}
              </p>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.includeMedia")}
                <Switch
                  checked={config.includeMedia || false}
                  onChange={(e) => setConfig({ ...config, includeMedia: e.target.checked })}
                  color="primary"
                />
              </label>
            </div>

            {config.includeMedia && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.fields.mediaUrl")}
                </label>
                <TextField
                  value={config.mediaUrl || ''}
                  onChange={(e) => setConfig({ ...config, mediaUrl: e.target.value })}
                  fullWidth
                  variant="outlined"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            )}
          </>
        );
      }

      if (subtype === 'email_smtp' || subtype === 'email_template') {
        return (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.emailSubject")}
              </label>
              <TextField
                value={config.subject || ''}
                onChange={(e) => setConfig({ ...config, subject: e.target.value })}
                fullWidth
                variant="outlined"
                placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.emailSubject")}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.emailBody")}
              </label>
              <TextField
                multiline
                rows={8}
                value={config.body || ''}
                onChange={(e) => setConfig({ ...config, body: e.target.value })}
                fullWidth
                variant="outlined"
                placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.emailBody")}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.fromEmail")}
              </label>
              <TextField
                value={config.fromEmail || 'noreply@klorum.com'}
                onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
                fullWidth
                variant="outlined"
              />
            </div>
          </>
        );
      }
    }

    // Logic Nodes
    if (type === 'logic') {
      if (subtype === 'condition') {
        return (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                {i18n.t("flowBuilderCanvas.nodeConfig.fields.conditionType")}
              </label>
              <Select
                value={config.conditionType || 'simple'}
                onChange={(e) => setConfig({ ...config, conditionType: e.target.value })}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="simple">{i18n.t("flowBuilderCanvas.nodeConfig.options.conditionType.simple")}</MenuItem>
                <MenuItem value="multiple">{i18n.t("flowBuilderCanvas.nodeConfig.options.conditionType.multiple")}</MenuItem>
                <MenuItem value="expression">{i18n.t("flowBuilderCanvas.nodeConfig.options.conditionType.expression")}</MenuItem>
              </Select>
            </div>

            {config.conditionType === 'simple' && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                    {i18n.t("flowBuilderCanvas.nodeConfig.fields.fieldToCheck")}
                  </label>
                  <TextField
                    value={config.field || ''}
                    onChange={(e) => setConfig({ ...config, field: e.target.value })}
                    fullWidth
                    variant="outlined"
                    placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.fieldToCheck")}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                    {i18n.t("flowBuilderCanvas.nodeConfig.fields.operator")}
                  </label>
                  <Select
                    value={config.operator || 'equals'}
                    onChange={(e) => setConfig({ ...config, operator: e.target.value })}
                    fullWidth
                    variant="outlined"
                  >
                    <MenuItem value="equals">{i18n.t("flowBuilderCanvas.nodeConfig.options.operator.equals")}</MenuItem>
                    <MenuItem value="notEquals">{i18n.t("flowBuilderCanvas.nodeConfig.options.operator.notEquals")}</MenuItem>
                    <MenuItem value="greater">{i18n.t("flowBuilderCanvas.nodeConfig.options.operator.greater")}</MenuItem>
                    <MenuItem value="less">{i18n.t("flowBuilderCanvas.nodeConfig.options.operator.less")}</MenuItem>
                    <MenuItem value="contains">{i18n.t("flowBuilderCanvas.nodeConfig.options.operator.contains")}</MenuItem>
                    <MenuItem value="startsWith">{i18n.t("flowBuilderCanvas.nodeConfig.options.operator.startsWith")}</MenuItem>
                    <MenuItem value="endsWith">{i18n.t("flowBuilderCanvas.nodeConfig.options.operator.endsWith")}</MenuItem>
                  </Select>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                    {i18n.t("flowBuilderCanvas.nodeConfig.fields.valueToCompare")}
                  </label>
                  <TextField
                    value={config.compareValue || ''}
                    onChange={(e) => setConfig({ ...config, compareValue: e.target.value })}
                    fullWidth
                    variant="outlined"
                    placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.valueToCompare")}
                  />
                </div>
              </>
            )}

            {config.conditionType === 'expression' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.fields.jsExpression")}
                </label>
                <TextField
                  multiline
                  rows={4}
                  value={config.expression || ''}
                  onChange={(e) => setConfig({ ...config, expression: e.target.value })}
                  fullWidth
                  variant="outlined"
                  placeholder="return input.age > 18 && input.country === 'CL';"
                />
              </div>
            )}
          </>
        );
      }
    }

    // Default Configuration
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>
        <Settings size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
        <p style={{ fontSize: 14 }}>{i18n.t("flowBuilderCanvas.nodeConfig.empty.noSpecificConfig")}</p>
        <p style={{ fontSize: 12, marginTop: 8 }}>{i18n.t("flowBuilderCanvas.nodeConfig.empty.connectHelp")}</p>
      </div>
    );
  };

  return (
    <div style={{
      width: 400,
      height: '100%',
      background: '#ffffff',
      borderLeft: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '-4px 0 12px rgba(0,0,0,0.08)'
    }}>
      {/* Header */}
      <div style={{
        padding: 20,
        borderBottom: '1px solid #e5e7eb',
        background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
            {node.data?.label}
          </h3>
          <Button
            onClick={onClose}
            style={{ minWidth: 32, padding: 8 }}
          >
            <X size={20} />
          </Button>
        </div>
        <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
          {node.type} • {node.data?.subtype}
        </p>
      </div>

      {/* Configuration Form */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: 20
      }}>
        {/* Basic Configuration Section */}
        <div style={{ marginBottom: 24 }}>
          <div
            onClick={() => toggleSection('basic')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              marginBottom: 16
            }}
          >
            {expandedSections.basic ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>
              {i18n.t("flowBuilderCanvas.nodeConfig.sections.basic")}
            </h4>
          </div>

          {expandedSections.basic && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.fields.nodeName")}
                </label>
                <TextField
                  value={node.data?.label || ''}
                  onChange={(e) => onSave(node.id, { ...node.data, label: e.target.value })}
                  fullWidth
                  variant="outlined"
                  placeholder={i18n.t("flowBuilderCanvas.nodeConfig.placeholders.nodeName")}
                />
              </div>

              {renderConfigByType()}
            </div>
          )}
        </div>

        {/* Advanced Options */}
        <div style={{ marginBottom: 24 }}>
          <div
            onClick={() => toggleSection('advanced')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              marginBottom: 16
            }}
          >
            {expandedSections.advanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>
              {i18n.t("flowBuilderCanvas.nodeConfig.sections.advanced")}
            </h4>
          </div>

          {expandedSections.advanced && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.fields.continueOnError")}
                  <Switch
                    checked={config.continueOnError || false}
                    onChange={(e) => setConfig({ ...config, continueOnError: e.target.checked })}
                    color="primary"
                  />
                </label>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
                  {i18n.t("flowBuilderCanvas.nodeConfig.fields.retryOnFailure")}
                </label>
                <TextField
                  type="number"
                  value={config.retries || 0}
                  onChange={(e) => setConfig({ ...config, retries: parseInt(e.target.value) })}
                  fullWidth
                  variant="outlined"
                  placeholder="0"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: 20,
        borderTop: '1px solid #e5e7eb',
        background: '#f9fafb',
        display: 'flex',
        gap: 12
      }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save size={16} />}
          onClick={handleSave}
          style={{ flex: 1 }}
        >
          {i18n.t("common.save")}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Copy size={16} />}
          onClick={() => onDuplicate(node.id)}
        >
          {i18n.t("common.duplicate")}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<Trash2 size={16} />}
          onClick={() => {
            if (window.confirm(i18n.t("flowBuilderCanvas.nodeConfig.confirmDelete"))) {
              onDelete(node.id);
              onClose();
            }
          }}
        >
          {i18n.t("common.delete")}
        </Button>
      </div>
    </div>
  );
};

export default NodeConfigPanel;
